import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, Sparkles, ShieldCheck } from 'lucide-react';
import Logo from '../components/Logo';

const JoinGroup: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'joining' | 'error'>('checking');

  useEffect(() => {
    handleInvite();
  }, [code]);

  const handleInvite = async () => {
    if (!code) return;

    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      // Not logged in: Save code and go to login
      sessionStorage.setItem('pending_invite_code', code);
      navigate('/login');
      return;
    }

    // Logged in: Attempt to join immediately
    setStatus('joining');
    try {
      const { data: hh } = await supabase.from('households').select('id, name').eq('invite_code', code.toUpperCase()).single();
      
      if (!hh) {
        setStatus('error');
        return;
      }

      const { error: joinError } = await supabase.from('household_members').upsert({
        household_id: hh.id,
        user_id: session.user.id
      });

      if (joinError) throw joinError;
      
      navigate(`/groups/${hh.id}`);
    } catch (e) {
      console.error(e);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-8">
      <div className="text-center max-w-sm">
        <Logo size={80} className="mx-auto mb-8 animate-bounce" />
        {status === 'checking' || status === 'joining' ? (
          <>
            <h1 className="text-2xl font-black text-white mb-4">Checking Invite...</h1>
            <div className="flex items-center justify-center gap-2 text-amber-400">
               <Loader2 className="animate-spin" size={20} />
               <span className="text-[10px] font-black uppercase tracking-[0.3em]">Opening your invite</span>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-black text-red-400 mb-4">Invalid Link</h1>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest leading-relaxed">
              This invitation link may have expired or is incorrect. Please ask the group owner for a new referral.
            </p>
            <button onClick={() => navigate('/')} className="mt-8 bg-white/10 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-white/5">
              Return to Safety
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default JoinGroup;
